import { formatNumber, isFiniteNumber } from '../math.js';

export const COLLISION_DIAMETER = 0.6;
export const TRACK_HALF_LENGTH = 5;

export function kineticEnergy(mass, velocity) {
  return 0.5 * mass * velocity * velocity;
}

/**
 * 一维恢复系数碰撞的解析解。
 * e = 1 为完全弹性，e = 0 为完全非弹性。
 */
export function collisionOutcome(m1, m2, v1, v2, e = 1) {
  const totalMass = m1 + m2;
  const relative = v1 - v2;
  const v1After = ((m1 - e * m2) * v1 + (1 + e) * m2 * v2) / totalMass;
  const v2After = ((m2 - e * m1) * v2 + (1 + e) * m1 * v1) / totalMass;
  const energyLoss = 0.5 * ((m1 * m2) / totalMass) * (1 - e * e) * relative * relative;
  return {
    v1: v1After,
    v2: v2After,
    momentum: m1 * v1After + m2 * v2After,
    energyLoss,
  };
}

function energyOf(state, params) {
  const kinetic = kineticEnergy(params.mass1, state.v1) + kineticEnergy(params.mass2, state.v2);
  return { kinetic, potential: 0, total: kinetic };
}

function momentumOf(state, params) {
  return params.mass1 * state.v1 + params.mass2 * state.v2;
}

export const collision = {
  id: 'collision',
  order: 2,
  meta: {
    title: '弹性碰撞',
    shortTitle: '碰撞',
    eyebrow: 'MOMENTUM & IMPULSE',
    tagline: '在一维碰撞中寻找不变量',
    description:
      '调节两滑块的质量、初速度与恢复系数，逐帧观察碰撞前后速度变化，实时核验总动量与动能是否守恒。',
    accent: '#fbbf24',
    accentRgb: '251, 191, 36',
    grade: '高中物理 · 动量守恒',
    duration: '建议 10–15 分钟',
    objectives: [
      '理解一维碰撞中系统动量守恒的条件，会用动量守恒定律解决碰撞问题。',
      '区分弹性碰撞与非弹性碰撞，计算碰撞前后的总动能变化。',
      '掌握恢复系数的物理意义，验证质量相等的弹性碰撞存在速度交换。',
      '学会用气垫导轨和光电门测量碰撞前后的速度。',
    ],
    apparatus: ['气垫导轨', '两辆质量可调滑块', '光电门计时器', '天平', '米尺', 'PhysLab 仿真终端'],
    theory: [
      '系统在水平方向不受外力（或外力远小于内力）时，碰撞前后总动量守恒：$m_1v_1+m_2v_2=m_1v_1\'(t)+m_2v_2\'(t)$。',
      '恢复系数定义为 $e=\\dfrac{v_2\'-v_1\'}{v_1-v_2}$。完全弹性碰撞 $e=1$，完全非弹性碰撞 $e=0$。',
      '一维碰撞后速度的解析解：$v_1\'=\\dfrac{(m_1-em_2)v_1+(1+e)m_2v_2}{m_1+m_2}$，$v_2\'=\\dfrac{(m_2-em_1)v_2+(1+e)m_1v_1}{m_1+m_2}$。',
      '完全弹性碰撞中动能守恒；$e<1$ 时损失的动能 $\\Delta E_k=\\dfrac12\\dfrac{m_1m_2}{m_1+m_2}(1-e^2)(v_1-v_2)^2$。',
      '碰撞过程内力远大于外力，动量在极短时间内近似守恒；但机械能可以转化为内能、声能等，因此不一定守恒。',
    ],
    procedure: [
      '调平气垫导轨，使滑块在导轨上可近似匀速运动。',
      '用天平测量两滑块质量 $m_1$、$m_2$，记录滑块宽度。',
      '设置两滑块相向运动，用光电门记录碰撞前后的速度。',
      '分别进行完全弹性（加装弹性缓冲器）与非弹性（贴橡皮泥）碰撞。',
      '计算碰撞前后总动量与总动能，比较相对误差。',
      '改变质量比，重复实验，观察速度交换与能量转移规律。',
    ],
    safety: [
      '气垫导轨必须调平，否则重力分量会引入额外外力。',
      '滑块速度不宜过大，避免越出导轨或损坏缓冲器。',
      '碰撞前确认光电门位置已避开滑块的运动盲区。',
    ],
    formulas: [
      { label: '动量守恒', latex: 'm_1v_1+m_2v_2=m_1v_1\'+m_2v_2\'', note: '水平方向无外力' },
      { label: '恢复系数', latex: 'e=\\dfrac{v_2\'-v_1\'}{v_1-v_2}', note: '0 ≤ e ≤ 1' },
      { label: '碰撞后速度', latex: 'v_1\'=\\dfrac{(m_1-em_2)v_1+(1+e)m_2v_2}{m_1+m_2}', note: '一维正碰' },
      { label: '动能损失', latex: '\\Delta E_k=\\dfrac{m_1m_2}{2(m_1+m_2)}(1-e^2)(v_1-v_2)^2', note: 'e < 1 时' },
    ],
    recordTable: {
      title: '碰撞前后动量—动能记录表',
      columns: ['次数', 'm₁ / kg', 'm₂ / kg', 'v₁ / (m·s⁻¹)', 'v₂ / (m·s⁻¹)', 'v₁′ / (m·s⁻¹)', 'v₂′ / (m·s⁻¹)', 'p前 / (kg·m·s⁻¹)', 'p后 / (kg·m·s⁻¹)'],
      rows: 4,
    },
  },
  schema: [
    {
      key: 'mass1',
      label: '滑块 A 质量',
      symbol: 'm₁',
      unit: 'kg',
      min: 0.2,
      max: 10,
      step: 0.1,
      default: 2,
      precision: 1,
      group: '滑块参数',
      apply: 'reset',
      description: '滑块 A 的质量。质量比决定碰撞后的速度分配。',
    },
    {
      key: 'mass2',
      label: '滑块 B 质量',
      symbol: 'm₂',
      unit: 'kg',
      min: 0.2,
      max: 10,
      step: 0.1,
      default: 1,
      precision: 1,
      group: '滑块参数',
      apply: 'reset',
      description: '滑块 B 的质量。两质量相等时，完全弹性碰撞会交换速度。',
    },
    {
      key: 'velocity1',
      label: 'A 初速度',
      symbol: 'v₁',
      unit: 'm/s',
      min: 0.5,
      max: 8,
      step: 0.1,
      default: 3,
      precision: 1,
      group: '初始条件',
      apply: 'reset',
      description: '滑块 A 碰撞前的速度，取向右为正方向。',
    },
    {
      key: 'velocity2',
      label: 'B 初速度',
      symbol: 'v₂',
      unit: 'm/s',
      min: -8,
      max: 0,
      step: 0.1,
      default: -1,
      precision: 1,
      group: '初始条件',
      apply: 'reset',
      description: '滑块 B 碰撞前的速度。取负值表示向左运动，与 A 相向接近。',
    },
    {
      key: 'restitution',
      label: '恢复系数',
      symbol: 'e',
      unit: '',
      min: 0.2,
      max: 1,
      step: 0.05,
      default: 1,
      precision: 2,
      group: '碰撞材料',
      apply: 'reset',
      description: 'e = 1 为完全弹性碰撞，动能守恒；e < 1 时部分动能转化为内能。',
    },
  ],
  createState(params) {
    return {
      t: 0,
      x1: -3.2,
      x2: 1.6,
      v1: params.velocity1,
      v2: params.velocity2,
      collisions: 0,
      lastCollisionAt: null,
      lastCollisionX: null,
    };
  },
  advance(state, params, dt) {
    const { mass1, mass2, restitution } = params;
    let { x1, x2, v1, v2, collisions, lastCollisionAt, lastCollisionX } = state;
    let remaining = dt;

    const closingSpeed = v1 - v2;
    const gap = x2 - x1 - COLLISION_DIAMETER;
    let collided = false;

    if (closingSpeed > 0) {
      if (gap <= 0) {
        const outcome = collisionOutcome(mass1, mass2, v1, v2, restitution);
        v1 = outcome.v1;
        v2 = outcome.v2;
        x1 += v1 * remaining;
        x2 += v2 * remaining;
        remaining = 0;
        collided = true;
      } else {
        const timeToContact = gap / closingSpeed;
        if (timeToContact <= remaining) {
          x1 += v1 * timeToContact;
          x2 += v2 * timeToContact;
          remaining -= timeToContact;
          const outcome = collisionOutcome(mass1, mass2, v1, v2, restitution);
          v1 = outcome.v1;
          v2 = outcome.v2;
          x1 += v1 * remaining;
          x2 += v2 * remaining;
          remaining = 0;
          collided = true;
        }
      }
    }

    if (!collided || remaining > 0) {
      if (remaining === dt) {
        x1 += v1 * dt;
        x2 += v2 * dt;
      }
    }

    if (collided) {
      collisions += 1;
      lastCollisionAt = state.t + dt;
      lastCollisionX = (x1 + x2) / 2;
      // 防止浮点误差导致两滑块视觉重叠。
      if (x2 - x1 < COLLISION_DIAMETER) {
        const mid = (x1 + x2) / 2;
        x1 = mid - COLLISION_DIAMETER / 2;
        x2 = mid + COLLISION_DIAMETER / 2;
      }
    }

    return {
      t: state.t + dt,
      x1,
      x2,
      v1,
      v2,
      collisions,
      lastCollisionAt,
      lastCollisionX,
    };
  },
  measure(state, params) {
    const { mass1, mass2, velocity1, velocity2, restitution } = params;
    const energy = energyOf(state, params);
    const momentum = momentumOf(state, params);
    const totalMass = mass1 + mass2;
    const comVelocity = momentum / totalMass;
    const predicted = collisionOutcome(mass1, mass2, velocity1, velocity2, restitution);
    const initialEnergy = kineticEnergy(mass1, velocity1) + kineticEnergy(mass2, velocity2);
    const status = state.collisions === 0
      ? { tone: 'info', label: '碰撞前', detail: '两滑块正在接近，总动量保持不变' }
      : restitution >= 0.999
        ? { tone: 'ok', label: '完全弹性碰撞', detail: '动量与动能同时守恒' }
        : { tone: 'warning', label: '非弹性碰撞', detail: '动量守恒，部分动能转化为内能' };

    return {
      time: state.t,
      energy,
      momentum: { total: momentum, unit: 'kg·m/s' },
      status,
      readouts: [
        { key: 'v1', label: 'A 速度', value: state.v1, unit: 'm/s', precision: 2 },
        { key: 'v2', label: 'B 速度', value: state.v2, unit: 'm/s', precision: 2 },
        { key: 'p', label: '总动量', value: momentum, unit: 'kg·m/s', precision: 2 },
        { key: 'ke', label: '总动能', value: energy.kinetic, unit: 'J', precision: 2 },
        { key: 'vcm', label: '质心速度', value: comVelocity, unit: 'm/s', precision: 2 },
        { key: 'collisions', label: '碰撞次数', value: state.collisions, unit: '次', precision: 0 },
      ],
      extras: {
        v1: state.v1,
        v2: state.v2,
        momentum,
        kinetic: energy.kinetic,
        comVelocity,
        predictedV1: predicted.v1,
        predictedV2: predicted.v2,
        predictedEnergyLoss: predicted.energyLoss,
        initialEnergy,
        energyLoss: initialEnergy - energy.kinetic,
        restitution,
        totalMass,
        isElastic: restitution >= 0.999,
        comPosition: (mass1 * state.x1 + mass2 * state.x2) / totalMass,
      },
    };
  },
  reference(state, params) {
    return {
      energy: energyOf(state, params).total,
      momentum: momentumOf(state, params),
    };
  },
  conservation(params) {
    return {
      energy: params.restitution >= 0.999 ? 'conserved' : 'dissipative',
      momentum: 'conserved',
    };
  },
  isFinished(state) {
    // 碰撞完成后任一滑块离开观测轨道即停止采集，避免课堂画面出现贴边裁剪。
    if (state.collisions > 0) {
      return (
        state.x1 > TRACK_HALF_LENGTH + 0.15 ||
        state.x1 < -TRACK_HALF_LENGTH - 0.15 ||
        state.x2 > TRACK_HALF_LENGTH + 0.15 ||
        state.x2 < -TRACK_HALF_LENGTH - 0.15
      );
    }
    return (
      (state.x1 > TRACK_HALF_LENGTH + 0.6 && state.x2 > TRACK_HALF_LENGTH + 0.6) ||
      (state.x1 < -TRACK_HALF_LENGTH - 0.6 && state.x2 < -TRACK_HALF_LENGTH - 0.6)
    );
  },
  sanitizeParams(params) {
    const next = { ...params };
    if (!isFiniteNumber(next.restitution)) next.restitution = 1;
    if (next.velocity1 - next.velocity2 <= 0.05) {
      next.velocity2 = next.velocity1 - 0.5;
      if (next.velocity2 < -8) {
        next.velocity2 = -8;
        next.velocity1 = -7.5;
      }
    }
    return next;
  },
  describe(params) {
    const predicted = collisionOutcome(
      params.mass1,
      params.mass2,
      params.velocity1,
      params.velocity2,
      params.restitution,
    );
    return `碰撞后预测：v₁′ = ${formatNumber(predicted.v1, 2)} m/s，v₂′ = ${formatNumber(predicted.v2, 2)} m/s`;
  },
};
